import { useChessboardStore } from '@strana-artw/chessboard';
import {
  defineStore,
  storeToRefs,
} from 'pinia';
import {
  computed,
  ref,
} from 'vue';

import { bookingApiConfig } from '@/config/api/booking';
import { useClientApi } from '@/plugins/api/lk';
import { useIsomorphicRoute } from '@/plugins/app-context';
import type { ConsultationType } from '@/shared/constants';
import { decorateApiActions } from '@/shared/lib';
import { useAuthStore } from '@/store/authStore';
import { useBuildingsStore } from '@/store/buildings';
import type { ClientWarmthValue } from '@/store/projects';

interface CreateObjectPayload {
  propertyId: string;
  type: string;
  bookingTypeId: number;
}

interface CreateBookingByPinningPayload {
  userId: number;
  activeProject: number;
  consultationType: ConsultationType;
  clientAlreadyAssigned?: boolean;
  withObject: boolean;
  clientWarmth?: ClientWarmthValue;
  isPromoLead?: boolean | null;
}

interface CreateFreeBookingByPinningPayload {
  profitbaseId: number;
  bookingId: number;
  commentText: string;
  categoriesBookingIds: Array<number>;
}

interface BookRealtyPayload {
  propertyId: number;
  paymentMethodSlug: string;
  mortgageType: boolean;
  mortgageProgramName: string;
  calculatorOptions: string;
  bookingId: number;
  bookingTypeId: number;
}

type ISODateString = string;

interface BookRealtyResponse {
  id: number;
  active: boolean;
  propertyId: number;
  expires: ISODateString;
  created: ISODateString;
}

export interface BookingError {
  reason?: string;
  message: string;
}

interface BookingResult {
  data: BookRealtyResponse;
  status: string;
  error?: BookingError;
}

export const STAGE_STEPS = {
  RealtyDataStep: 'RealtyDataStep',
  BookingParamsStep: 'BookingParamsStep',
  BookingResultStep: 'BookingResultStep',
};

const steps = [
  {
    component: 'SelectedDealsBookingProjects',
    label: 'Выберите ЖК',
  },
  {
    component: 'SelectedDealsBookingChessboard',
  },
];

const modalSteps = [
  STAGE_STEPS.RealtyDataStep,
  STAGE_STEPS.BookingParamsStep,
  STAGE_STEPS.BookingResultStep,
];

const STAGES = {
  SelectedDealsBookingProjects: 1,
  SelectedDealsBookingChessboard: [2, 3],
  RealtyDataStep: 4,
  BookingParamsStep: 5,
};

const headersParams = {
  'deals-id-booking': 'deals',
  'deals-id-catalog': 'deals',
  'choice-apartment-chessboard-project': 'choice-apartment',
  'choice-apartment-project': 'choice-apartment',
  'choice-apartment-catalog': 'apartment-catalog',
  'apartment-catalog': 'apartment-catalog',
};

export const useBookingStore = defineStore('booking', () => {
  const currentStepIndex = ref<number>(0);
  const currentModalStepIndex = ref<number>(0);
  const bookingResult = ref<BookingResult | null>(null);

  const currentStep = computed(() => steps[currentStepIndex.value]);
  const currentModalStep = computed(
    () => modalSteps[currentModalStepIndex.value],
  );
  const stage = (value: keyof typeof STAGES) => STAGES[value];
  const stageCount = computed(() => Object.values(STAGES).reduce((previousValue: number, currentValue) => {
    if (Array.isArray(currentValue)) {
      return previousValue + currentValue.length;
    }

    return ++previousValue;
  }, 0),
  );

  function setInitialValue (): void {
    currentStepIndex.value = 0;
    const chessboardStore = useChessboardStore();
    chessboardStore.resetFilter();
    chessboardStore.updateFilter('project', '');
    chessboardStore.updateFilter('building', '');
    chessboardStore.setActiveFlatId('');
    useBuildingsStore().reset();
  }

  function setModalInitialValue (): void {
    currentModalStepIndex.value = 0;
    bookingResult.value = null;
  }

  function setCurrentStep (payload: string): void {
    currentStepIndex.value = steps.findIndex(
      ({ component }) => component === payload,
    );
  }

  function setCurrentModalStep (payload: string): void {
    currentModalStepIndex.value = modalSteps.indexOf(payload);
  }

  function setBookingResult (payload: BookingResult): void {
    bookingResult.value = payload;
  }

  async function createObject (payload: CreateObjectPayload): Promise<number> {
    const { propertyId, type, bookingTypeId } = payload;

    if (!propertyId) {
      throw new Error('global_id is undefined');
    }
    const url = bookingApiConfig.properties();
    const createObjectData = await useClientApi().$post(url, {
      global_id: propertyId,
      type,
      booking_type_id: bookingTypeId,
    });

    if (!createObjectData?.id) {
      throw new Error('object id is undefined');
    }

    return createObjectData.id;
  }

  async function createBookingByPinning (
    payload: CreateBookingByPinningPayload,
  ): Promise<{ bookingId: number }> {
    const authStore = useAuthStore();

    const { role } = storeToRefs(authStore);

    if (role.value) {
      const url = bookingApiConfig.createBooking(role.value);
      const { data } = await useClientApi().post(url, payload);

      return { bookingId: data.id };
    } else {
      throw new Error(`User role is not defined: ${ role.value } `);
    }
  }

  async function createFreeBookingByPinning (
    payload: CreateFreeBookingByPinningPayload,
  ): Promise<void> {
    const $route = useIsomorphicRoute();

    const url = bookingApiConfig.freeBooking();
    await useClientApi().post(url, payload, {
      headers: {
        'X-Page-URL':
          headersParams[$route.value.name as keyof typeof headersParams] ?? '',
      },
    });
  }

  async function bookRealty (
    payload: BookRealtyPayload,
  ): Promise<BookRealtyResponse> {
    const $route = useIsomorphicRoute();
    const url = bookingApiConfig.bookRealty();

    return useClientApi().$patch(url, payload, {
      headers: {
        'X-Page-URL':
          headersParams[$route.value.name as keyof typeof headersParams] ?? '',
      },
    });
  }

  async function unbookingProperty (payload: { bookingId: number }) {
    const url = bookingApiConfig.unbookingProperty();
    await useClientApi().$patch(url, payload);
  }

  const decoratedApiActionsRethrow = decorateApiActions(
    {
      createObject,
      createBookingByPinning,
      createFreeBookingByPinning,
      bookRealty,
      unbookingProperty,
    },
    'store/deal/booking',
    true,
  );

  return {
    currentStepIndex,
    currentModalStepIndex,
    bookingResult,
    currentStep,
    currentModalStep,
    stage,
    stageCount,
    setInitialValue,
    setModalInitialValue,
    setCurrentStep,
    setCurrentModalStep,
    setBookingResult,
    ...decoratedApiActionsRethrow,
  };
});
